import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, tap } from "rxjs/operators";

import * as CardsActionTypes from "../../actions/cards/cards.actions";
import * as PromptsActionTypes from "../../actions/prompts/prompts.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { PromptService } from "src/app/shared/services/prompts/prompts.service";
import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class PromptsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private promptService: PromptService,
    private categoryService: CategoriesService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}
  getPrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.getPrompts),
      exhaustMap((action) =>
        this.promptService
          .getPrompts(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PromptsActionTypes.loadPrompts({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PromptsActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("Prompts.loadError", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getExcelPrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.getExcelPrompts),
      exhaustMap((action) =>
        this.promptService
          .getPrompts(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PromptsActionTypes.loadExcelPrompts({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PromptsActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("Prompts.loadError", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.getPrompt),
      exhaustMap((action) =>
        this.promptService.getPrompt(action.code).pipe(
          map((result) => PromptsActionTypes.setPrompt({ prompt: result })),
          catchError((error) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.loadPromptError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getDataTypePrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.getDataTypePrompts),
      exhaustMap((action) =>
        this.promptService.getDataTypePrompts(action.pageReq).pipe(
          map((result) =>
            PromptsActionTypes.loadDataTypePrompts({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.loadPromptDataTypeError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  getCategoriesPrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.getCategoriesPrompts),
      exhaustMap((action) =>
        this.categoryService.getCategories(action.pageReq).pipe(
          map((result) =>
            PromptsActionTypes.loadCategoriesPrompts({ response: result })
          ),
          catchError((error) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.loadCategoryPromptsError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Prompts Table Config
  getPromptsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.getPromptsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              PromptsActionTypes.loadPromptsTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PromptsActionTypes.loadPromptsTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Prompts Table Config
  savePromptsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.savePromptsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            PromptsActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.createPrompts),
      exhaustMap((action) =>
        this.promptService.createPrompts(action).pipe(
          mergeMap((result) => [
            PromptsActionTypes.resetCreation(),
            PromptsActionTypes.getPrompt({ code: result?.result?.code }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.promptCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Prompts.promptCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  updatePrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.updatePrompts),
      exhaustMap((action) =>
        this.promptService.updatePrompts(action.prompt).pipe(
          map((result) => {
            return PromptsActionTypes.getPrompt({ code: result?.result?.code });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.promptUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.promptUpdatedFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  setDisabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.setDisabled),
      exhaustMap((action) =>
        this.promptService.deletePrompt(action).pipe(
          map((result) =>
            PromptsActionTypes.getPrompts({
              options: {
                paging: {
                  skip: 0,
                  top: 99999,
                },
                filteringArgs: undefined,
                sortingArgs: undefined,
              },
            })
          ),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.setDisabled",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.propertyDeletedFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  setEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromptsActionTypes.setEnabled),
      exhaustMap((action) =>
        this.promptService.updatePrompts(action).pipe(
          map((result) => {
            this.store$.dispatch(
              PromptsActionTypes.getPrompt({ code: result?.result?.code })
            );
            return PromptsActionTypes.getPrompts({
              options: {
                paging: {
                  skip: 0,
                  top: 99999,
                },
                filteringArgs: undefined,
                sortingArgs: undefined,
              },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj("Prompts.setEnable", SnackbarType.SUCCESS)
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PromptsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Prompts.promptsUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
